import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon, DoneAll as AllIcon, LocalShippingOutlined as TruckIcon } from '@mui/icons-material';
import { comptoirServicesAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { ComptoirLivraison, ComptoirServiceDetail } from '../../../types/comptoir';
import { COLOR, NumberField, dh, num, primaryBtnSx, r2, today } from '../ui';

interface Props {
  open: boolean;
  service: ComptoirServiceDetail;
  onClose: () => void;
  onSaved: (lv: ComptoirLivraison) => void;
}

/** Livrer: a delivery note (BLC) with a quantity per job, partial deliveries allowed. */
const LivrerDialog: React.FC<Props> = ({ open, service, onClose, onSaved }) => {
  const restants = service.travaux.filter((t) => num(t.reste_a_livrer) > 0);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);

  const fillAll = () => setQty(Object.fromEntries(restants.map((t) => [t.id, String(r2(num(t.reste_a_livrer)))])));
  useEffect(() => {
    if (!open) return;
    fillAll(); setDate(today()); setNote(''); setSubmitted(false); setServerErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const over = restants.find((t) => num(qty[t.id]) > num(t.reste_a_livrer) + 0.0001);
  const any = restants.some((t) => num(qty[t.id]) > 0);
  const err = over ? `« ${over.libelle} » : il ne reste que ${num(over.reste_a_livrer)} à livrer.`
    : !any ? 'Indiquez au moins une quantité à livrer.' : '';
  const partial = restants.some((t) => num(qty[t.id]) < num(t.reste_a_livrer));

  const handleSave = async () => {
    setSubmitted(true);
    setServerErr('');
    if (err) return;
    setSaving(true);
    try {
      const lv = await comptoirServicesAPI.livrer(service.id, {
        date, note: note.trim() || null,
        lignes: restants.filter((t) => num(qty[t.id]) > 0).map((t) => ({ travail_id: t.id, quantite: r2(num(qty[t.id])) })),
      });
      onSaved(lv);
    } catch (e: any) {
      setServerErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="comptoir-liv-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha('#7B1FA2', 0.12), color: '#7B1FA2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TruckIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography id="comptoir-liv-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Livrer {service.reference}</Typography>
          <Typography variant="body2" color="text.secondary">{service.client_nom} · {service.titre}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        {Number(service.reste) > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Reste dû : <b>{dh(service.reste)}</b>. La livraison n'efface pas ce montant.
          </Alert>
        )}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: submitted && err ? 'error.main' : 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, bgcolor: alpha(COLOR, 0.05) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Quantités livrées maintenant</Typography>
            <Button size="small" startIcon={<AllIcon />} onClick={fillAll} sx={{ color: COLOR }}>Tout le reste</Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Travail</TableCell><TableCell align="right">Commandé</TableCell>
                <TableCell align="right">Déjà livré</TableCell><TableCell align="right" width={160}>À livrer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {restants.map((t) => {
                const bad = num(qty[t.id]) > num(t.reste_a_livrer) + 0.0001;
                return (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.libelle}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.type_service}</Typography>
                    </TableCell>
                    <TableCell align="right">{num(t.quantite)} {t.unite}</TableCell>
                    <TableCell align="right">{num(t.livre)}</TableCell>
                    <TableCell align="right">
                      <NumberField size="small" value={qty[t.id] || ''} unit={t.unite} error={bad}
                        onChange={(v) => setQty((q) => ({ ...q, [t.id]: v }))}
                        helperText={bad ? `max ${num(t.reste_a_livrer)}` : undefined}
                        inputProps={{ 'aria-label': `Quantité à livrer pour ${t.libelle}` }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {submitted && err && <Typography variant="body2" color="error" sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>{err}</Typography>}
        </Paper>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField label="Date de livraison" type="date" fullWidth InputLabelProps={{ shrink: true }} value={date}
              onChange={(e) => setDate(e.target.value)} helperText="N° BLC attribué à la confirmation" />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField label="Contrôle et réserves" fullWidth placeholder="Conformité vérifiée, réserves éventuelles…"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{any && !over ? (partial ? 'Livraison partielle' : 'Livraison complète') : ''}</Typography>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 200 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <TruckIcon />}>
          {saving ? 'Enregistrement…' : 'Confirmer la livraison'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LivrerDialog;
